<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import UploadApi from 'dashboard/api/upload';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  initialParams: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const isUploading = ref(false);
const fileInputRef = ref(null);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const variablePresetOptions = [
  { value: '', label: 'Texto livre' },
  { value: 'nome', label: 'Nome do contato' },
  { value: 'saudacao', label: 'Saudação (Bom dia / Boa tarde / Boa noite)' },
  {
    value: 'cumprimento',
    label: 'Saudação + Nome (ex: Bom dia, João!)',
  },
  { value: 'name', label: 'Nome completo' },
  { value: 'first_name', label: 'Primeiro nome' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
];

const selectedPresets = ref({});

const applyPreset = (key, presetValue) => {
  selectedPresets.value[key] = presetValue;
  if (presetValue) {
    processedParams.value.body[key] = presetValue;
  } else {
    processedParams.value.body[key] = '';
  }
};

const initializeTemplateParameters = () => {
  const params = buildTemplateParameters(props.template, hasMediaHeader.value);

  // Restore saved values from initialParams (edit mode)
  if (props.initialParams) {
    if (props.initialParams.header && params.header) {
      Object.assign(params.header, props.initialParams.header);
    }
    if (props.initialParams.body && params.body) {
      Object.keys(params.body).forEach(key => {
        if (props.initialParams.body[key] != null) {
          params.body[key] = props.initialParams.body[key];
        }
      });
    }
    if (props.initialParams.buttons && params.buttons) {
      params.buttons.forEach((btn, idx) => {
        if (props.initialParams.buttons[idx]?.parameter != null) {
          btn.parameter = props.initialParams.buttons[idx].parameter;
        }
      });
    }
  }

  processedParams.value = params;

  // Restore preset selections for body variables that match known presets
  if (props.initialParams?.body) {
    const presetValues = variablePresetOptions
      .map(o => o.value)
      .filter(Boolean);
    Object.keys(props.initialParams.body).forEach(key => {
      const savedValue = props.initialParams.body[key];
      if (presetValues.includes(savedValue)) {
        selectedPresets.value[key] = savedValue;
      }
    });
  }
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const triggerFileUpload = () => {
  fileInputRef.value?.click();
};

const handleFileUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type based on template header format
  const format = headerComponent.value?.format?.toLowerCase();
  const validTypes = {
    image: ['image/jpeg', 'image/png', 'image/webp'],
    video: ['video/mp4', 'video/3gpp'],
    document: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
  };

  if (validTypes[format] && !validTypes[format].includes(file.type)) {
    useAlert(t('WHATSAPP_TEMPLATES.PARSER.INVALID_FILE_TYPE'));
    return;
  }

  isUploading.value = true;
  try {
    const response = await UploadApi.uploadFile(file);
    const fileUrl = response.data?.file_url;
    if (fileUrl) {
      updateMediaUrl(fileUrl);
      if (format === 'document') {
        updateMediaName(file.name);
      }
      useAlert(t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_SUCCESS'));
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    useAlert(t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
    // Reset file input
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  isFormInvalid,
  headerComponent,
  renderedTemplate,
  v$,
  isUploading,
  updateMediaUrl,
  updateMediaName,
  triggerFileUpload,
  handleFileUpload,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center gap-2 mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
          <button
            type="button"
            :disabled="isUploading"
            class="flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 disabled:bg-slate-400 disabled:cursor-not-allowed rounded-lg transition-colors whitespace-nowrap"
            @click="triggerFileUpload"
          >
            <span
              v-if="isUploading"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-upload size-4" />
            {{ isUploading ? 'Enviando...' : 'Upload' }}
          </button>
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            :accept="
              headerComponent?.format?.toLowerCase() === 'image'
                ? 'image/jpeg,image/png,image/webp'
                : headerComponent?.format?.toLowerCase() === 'video'
                  ? 'video/mp4,video/3gpp'
                  : '.pdf,.doc,.docx,.xls,.xlsx'
            "
            @change="handleFileUpload"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center gap-2 mb-2.5"
        >
          <div class="w-48 flex-shrink-0">
            <ComboBox
              :model-value="selectedPresets[key] || ''"
              :options="variablePresetOptions"
              placeholder="Selecionar variável"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button]:text-xs [&>div>button]:py-1.5"
              @update:model-value="value => applyPreset(key, value)"
            />
          </div>
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
            :disabled="!!selectedPresets[key]"
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
